<template>
	<div class="page">

		<!-- NAVBAR -->
		<nav class="navbar">
			<a class="nav-logo">
				<v-img
					alt="Orixe Logo"
					:src="require('../assets/Logo_Header.svg')"
					contain
					width="150"
					height="40"
					class="mr-2"
				/>
			</a>
			<div class="nav-right">
				<a class="nav-login" href="/login">Login</a>
				<a class="nav-contact" href="https://bookings.cloud.microsoft/book/BookaDemo1@orixe.se/?ismsaljsauthenabled=true" target="_blank">SUPPPORT BOOK A DEMO</a>
			</div>
		</nav>

		<!-- KNOWLEDGE BASE -->
		<div class="kb">
			<h1>Knowledge Base</h1>
			<div class="grid">
				<div class="card" v-for="item in articles" :key="item.title">
					<span class="badge" :class="item.badgeClass">{{ item.badge }}</span>
					<h3>{{ item.title }}</h3>
					<p>{{ item.description }}</p>

					<!-- Multi document: opens the document explorer -->
					<div v-if="item.docs" class="read-row">
						<button class="browse" @click="openDocs(item)">
							Browse documents <span class="browse-arrow">→</span>
						</button>
						<span class="doc-count">{{ countLeaves(item) }} PDF guides</span>
					</div>

					<!-- No PDF here: jumps down to the FAQ and opens the first question -->
					<button
						v-else-if="item.action === 'faq'"
						class="read faq-link"
						@click="goToFaq"
					>
						View FAQ <span class="faq-arrow">↓</span>
					</button>

					<!-- Single document: behaves exactly like before -->
					<a
						v-else
						class="read"
						:href="item.link"
						:download="item.download"
					>
						Download PDF <v-icon color="#1d9e75" small>fas fa-download</v-icon>
					</a>
				</div>
			</div>
		</div>

		<!-- DOCUMENT EXPLORER MODAL -->
		<div class="overlay" v-if="modalOpen" @click.self="closeModal">
			<div class="modal">

				<div class="modal-head">
					<div class="breadcrumb">
						<span>Knowledge Base</span>
						<span class="sep">/</span>
						<span :class="{ current: !activeLeaf }">{{ activeArticle.title }}</span>
						<template v-if="activeParentLabel">
							<span class="sep">/</span>
							<span>{{ activeParentLabel }}</span>
						</template>
						<template v-if="activeLeaf">
							<span class="sep">/</span>
							<span class="current">{{ activeLeaf.title }}</span>
						</template>
					</div>
					<button class="close-btn" @click="closeModal" aria-label="Close">✕</button>
				</div>

				<div class="modal-body">

					<!-- SIDEBAR TREE -->
					<div class="sidebar">
						<div class="sidebar-top">
							<div class="total">{{ countLeaves(activeArticle) }} PDF guides</div>
						</div>

						<div class="tree">
							<template v-for="node in activeArticle.docs.items">

								<!-- Parent node with children -->
								<div v-if="node.children" :key="node.id" class="tree-group">
									<div class="tree-item parent" @click="toggleParent(node.id)">
										<span>{{ node.title }}</span>
										<span class="arrow" :class="{ open: expandedParents[node.id] }">›</span>
									</div>
									<div class="tree-children" v-show="expandedParents[node.id]">
										<div
											v-for="child in node.children"
											:key="child.id"
											class="tree-item child"
											:class="{ active: activeLeaf && activeLeaf.id === child.id }"
											@click="selectLeaf(child, node.title)"
										>
											{{ child.title }}
										</div>
									</div>
								</div>

								<!-- Leaf node, no children -->
								<div
									v-else
									:key="node.id"
									class="tree-item top-leaf"
									:class="{ active: activeLeaf && activeLeaf.id === node.id }"
									@click="selectLeaf(node, null)"
								>
									{{ node.title }}
								</div>

							</template>
						</div>
					</div>

					<!-- CONTENT PANE -->
					<div class="content-pane">
						<div class="content-scroll" v-if="activeLeaf">
							<div class="eyebrow">{{ activeArticle.badge }}</div>
							<h2>{{ activeLeaf.title }}</h2>
							<p class="body-text">{{ activeLeaf.blurb }}</p>

							<div class="download-center">
								<a
									class="btn-download"
									:href="activeLeaf.link"
									:download="activeLeaf.download"
								>
									Download PDF
								</a>
							</div>
						</div>
						<div class="content-empty" v-else>
							<p>Select a document on the left to download it here.</p>
						</div>
					</div>

				</div>
			</div>
		</div>

		<!-- FAQ -->
		<div class="faq" ref="faqSection">
		<h1>FAQ</h1>
		<div v-for="(faq, i) in faqs" :key="i" class="faq-item">
			<div class="faq-header" @click="toggle(i)">
			<span>{{ faq.question }}</span>
			<span class="arrow" :class="{ open: openIndex === i }">›</span>
			</div>
			<div v-if="openIndex === i" class="faq-body">
			<p>{{ faq.answer }}</p>
			</div>
		</div>
		</div>

		<!-- FOOTER -->
		<footer>
		<div>
			<div class="flogo">✦ Orixe</div>
			<p>📧 orixe@orixe.se</p>
			<p>📞 +46 760072566, WhatsApp</p>
			<p>📍 Nääs Fabriker, Gothenburg</p>
		</div>
		<div>
			<h4>Modules</h4>
			<a>Audit</a>
			<a>Trace</a>
			<a>Sustainability</a>
			<a>CO₂ Calculator</a>
		</div>
		<div>
			<h4>Technology</h4>
			<a>Blockchain</a>
		</div>
		<div></div>
		</footer>

	</div>
</template>

<script>
export default {
	name: "KnowledgeBase",
	data() {
		return {
			openIndex: 0,

			// Document explorer state
			modalOpen: false,
			activeArticle: null,
			activeLeaf: null,
			activeParentLabel: null,
			expandedParents: {},

			articles: [
				// --- MULTI-DOCUMENT ARTICLE: replace every link/download below with the real PDF paths ---
				{ badge: "Platform",
					badgeClass: "green",
					title: "Registration, Login, Inviting Suppliers",
					description: "How to login to the Orixe dashboard, and invite suppliers to use the platform.",
					docs: {
						items: [
							{ id: "register-login", title: "Register & Login",
								blurb: "How to register an account and log in to the Orixe platform.",
								link: "/Registering-to-Orixe-platform.pdf", download: "Registering to Orixe platform.pdf" 
							},
							{ id: "sender", title: "Sender", children: [
								{ id: "invite-suppliers", title: "How to Invite Suppliers to the Orixe Platform",
									blurb: "Step-by-step instructions for sending a platform invitation to a new supplier.",
									link: "How-to-invite-suppliers-to-Orixe.pdf", download: "How to invite suppliers.pdf" },
								{ id: "send-connection", title: "How to Send Connection Requests to Existing Users on the Platform",
									blurb: "How to reach out and connect with companies that already use Orixe.",
									link: "/How-to-send-connection-requests to existing users.pdf", download: "How to send connection requests.pdf" },
							]},
							{ id: "recipient", title: "Recipient", children: [
								{ id: "accept-invite", title: "How Suppliers Accept Invitations on Orixe Platform",
									blurb: "What a supplier sees and does when they receive a platform invitation.",
									link: "/How-suppliers-accept-new-invitations.pdf", download: "How suppliers accept new invitations.pdf" },
								{ id: "accept-connection", title: "How Existing Users Accept Connection Requests",
									blurb: "How an existing user reviews and accepts an incoming connection request.",
									link: "/How-existing-users-accept-invitations.pdf", download: "How existing users accept invitations.pdf" },
							]},
						]
					}
				},

				// --- NO PDF: this one scrolls down to FAQ and opens the first question ---
				{ badge: "Support",
					badgeClass: "green",
					title: "Contact Information",
					description: "Contact us for support: support@orixe.se. Frequently asked questions and user tips can be found here.",
					action: "faq"
				},

				{ badge: "Users",
					badgeClass: "green",
					title: "How to manage your team",
					description: "A guide on how to manage Users in the Orixe platform.",
					link: "/Manage-your-team.pdf",
					download: "Manage your team.pdf"
				},

				{ badge: "Trace",
					badgeClass: "teal",
					title: "Trace Tutorial",
					description: "Tutorial on how to get started with the Orixe trace module.",
					link: "/Trace-Tutorial.pdf",
					download: "Trace Tutorial.pdf"
				},
				{ badge: "Digital Audit",
					badgeClass: "teal",
					title: "Digital Audit Tutorial",
					description: "A step-by-step guide to the Orixe Digital Audit module.",
					docs: {
						items: [
							{ id: "create-send-audit", title: "Create and Send an Audit",
								blurb: "How to create an audit and send it to the relevant recipient.",
								link: "/How-to-create-and-send-an-Audit.pdf", download: "How to create and send an Audit.pdf" },
							{ id: "submit-audit", title: "Submit the Audit",
								blurb: "How to submit an audit once it has been completed.",
								link: "/How-to-submit-the-Audit.pdf", download: "How to Submit the Audit.pdf" },
						]
					}
				},
				{ badge: "Audit Flow-down",
					badgeClass: "teal",
					title: "Audit Flow-down Tutorial",
					description: "A step-by-step guide to the Audit Flow-down module.",
					docs: {
						items: [
							{ id: "create-flowdown-audit", title: "Create a Flowdown Audit",
								blurb: "How to create a flowdown audit.",
								link: "/How-to-Create-a-Flowdown-Audit.pdf", download: "How to Create a Flowdown Audit.pdf" },
							{ id: "submit-forward-flowdown-audit", title: "Submit and Forward a Flowdown Audit",
								blurb: "How to submit a flowdown audit and forward it to sub-suppliers.",
								link: "/Submit-a-Flowdown-Audit-and-forward-it.pdf", download: "Submit a Flowdown Audit and forward it.pdf" },
						]
					}
				},
				{ badge: "On-site Audit",
					badgeClass: "teal",
					title: "On-site Audit Tutorial",
					description: "A Step-by-Step Guide to the Onsite Audit Process.",
					link: "/Onsite-Audit-Tutorial.pdf",
					download: "Onsite Audit Tutorial.pdf"
				},

				// --- MULTI-DOCUMENT ARTICLE: replace every link/download below with the real PDF paths ---
				{ badge: "Sustainability",
					badgeClass: "teal",
					title: "Sustainability Tutorial",
					description: "Tutorial on how to use the Orixe sustainability module.",
					docs: {
						items: [
							{ id: "esg-survey", title: "ESG Survey",
								blurb: "How to log in, complete, save, and submit your ESG sustainability survey.",
								link: "/ESG-Survey.pdf", download: "ESG Survey.pdf" },
							{ id: "dashboard", title: "Dashboard",
								blurb: "An overview of your sustainability module — what it shows and how to read it.",
								link: "/Sustainability-Dashboard.pdf", download: "Sustainability Dashboard.pdf" },
							{ id: "esg-score", title: "ESG Score", children: [
								{ id: "value-chain", title: "Your Value Chain",
									blurb: "How your value chain map is built and how to keep it up to date.",
									link: "/Value-Chain-Guide.pdf", download: "Value Chain Guide.pdf" },
								{ id: "your-score", title: "Your Score",
									blurb: "How your overall ESG score is calculated and what each part of it means.",
									link: "/ESG-Score.pdf", download: "ESG Score.pdf" },
								{ id: "connections-score", title: "Connections Score",
									blurb: "How the scores of your connected suppliers factor into your own rating.",
									link: "/Connections-Score.pdf", download: "Connections Score.pdf" },
							]},
							{ id: "risk-matrix-cat", title: "Risk Matrix", children: [
								{ id: "risk-definition", title: "Risk Definition",
									blurb: "The criteria Orixe uses to define and categorise risk.",
									link: "/Risk-Definition.pdf", download: "Risk Definition.pdf" },
								{ id: "risk-matrix", title: "Risk Matrix",
									blurb: "How to read the risk matrix and what each position on it means.",
									link: "/Risk-Matrix.pdf", download: "Risk Matrix.pdf" },
							]},
							{ id: "due-diligence-wheel", title: "Due Diligence Wheel",
								blurb: "Where to find guides, FAQs, and support contact details inside the module.",
								link: "/Due-Diligence-Wheel.pdf", download: "Due Diligence Wheel.pdf" },
						]
					}
				},

				{ badge: "CO2",
					badgeClass: "teal",
					title: "CO2 Tutorial",
					description: "Tutorial on how to use the CO2 module.",
					link: "/CO2-Tutorial.pdf",
					download: "CO2 Tutorial.pdf"
				},
			],
			faqs: [
				{ question: "Do you provide service support?", answer: "Yes, we are here to support you and your company. If you run into any issues or problems, please call us at +47 90911419 or send us an email to support@orixe.se." },
				{ question: "What does Orixe do?", answer: "Orixe empowers businesses to be in full control of their supply chain. We deliver traceability and transparency of all transactions in the supply chain (Trace Module) and help businesses audit their suppliers more efficiently (Audit Module)." },
				{ question: "What guarantees can you give regarding confidentiality?", answer: "All data entered in Orixe is encrypted. You will be the only one who has the opportunity to read your data. No sensitive information can be shared through Orixe." },
				{ question: "How much traffic can the hosting handle?", answer: "Our hosting scales automatically to handle millions of concurrent visits, served through Amazon's Cloudfront CDN." },
				{ question: "Do all my clients need to have Orixe to get value from the product?", answer: "No. Even securing transactions with a single buyer or supplier can be extremely valuable to ensure ethical and safe trade." },
				{ question: "Can Orixe be integrated with my ERP-system?", answer: "Yes, the Orixe platform can easily be implemented with your ERP system. We use Standard API and have experience integrating with most major ERP systems." },
			],
		}
	},
	mounted() {
		this.openDocumentFromQuery()
	},
	methods: {
		toggle(i) {
			this.openIndex = this.openIndex === i ? null : i
		},

		// Scrolls down to the FAQ section and opens the first question
		goToFaq() {
			this.openIndex = 0
			this.$nextTick(() => {
				const el = this.$refs.faqSection
				if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
			})
		},

		// Counts the number of downloadable PDFs in a multi-document article
		countLeaves(article) {
			if (!article.docs) return 1
			let n = 0
			article.docs.items.forEach(node => {
				n += node.children ? node.children.length : 1
			})
			return n
		},

		openDocs(article) {
			this.activeArticle = article

			// Keep every dropdown group closed by default
			const expanded = {}
			article.docs.items.forEach(node => {
				if (node.children) expanded[node.id] = false
			})
			this.expandedParents = expanded

			// Nothing is selected until the person picks a document
			this.activeLeaf = null
			this.activeParentLabel = null

			this.modalOpen = true
		},

		toggleParent(id) {
			this.expandedParents = { ...this.expandedParents, [id]: !this.expandedParents[id] }
		},

		selectLeaf(leaf, parentLabel) {
			this.activeLeaf = leaf
			this.activeParentLabel = parentLabel
		},

		closeModal() {
			this.modalOpen = false
		},

		openDocumentFromQuery() {
			const { article, doc } = this.$route.query
			if (!article || !doc) {
				return
			}
			const selectedArticle = this.articles.find(
				item => item.title === article
			)
			if (!selectedArticle || !selectedArticle.docs) {
				return
			}
			this.activeArticle = selectedArticle
			const expanded = {}
			selectedArticle.docs.items.forEach(node => {
				if (node.children) {
					expanded[node.id] = false
				}
			})
			this.expandedParents = expanded
			const findDocument = (items, parent=null) => {
				for (const item of items) {
					if (item.id === doc) {
						return {
							leaf: item,
							parent
						}
					}
					if (item.children) {
						const result = findDocument(
							item.children,
							item.title
						)
						if (result) {
							return result
						}
					}
				}
				return null
			}
			const result = findDocument(
				selectedArticle.docs.items
			)
			if(result){
				this.activeLeaf = result.leaf
				this.activeParentLabel = result.parent
				if(result.parent){
					const parentNode = selectedArticle.docs.items.find(
						item => item.title === result.parent
					)
					if(parentNode){
						this.expandedParents = {
							...this.expandedParents,
							[parentNode.id]: true
						}
					}
				}
				this.modalOpen = true
			}
		},
	},
	watch: {
		$route() {
			this.openDocumentFromQuery()
		}
	},
}
</script>

<style scoped>
.page {
	background: #fff; color: #111; 
}

/* Navbar */
.navbar {
	display: flex;
	align-items: center;
	padding: 0 40px;
	height: 64px;
	border-bottom: 1px solid #eee;
	background: #fff;
	position: sticky;
	top: 0;
	z-index: 100;
}
.nav-logo {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 22px;
	font-weight: 800;
	color: #1a2c5b;
	text-decoration: none;
	margin-right: 32px;
}
.nav-links {
	display: flex;
	align-items: center;
	gap: 2px;
	flex: 1;
}
.nav-links a, .drop {
	font-size: 16px;
	color: #222;
	text-decoration: none;
	padding: 8px 14px;
	border-radius: 6px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}
.nav-links a:hover, .drop:hover {
	background: #f5f5f5;
}
.drop span {
	font-size: 11px;
	color: #888;
}
.nav-right {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;
}
.nav-login {
	font-size: 16px;
	color: #1d9e75;
	font-weight: 600;
	text-decoration: none;
	padding: 8px 14px;
}
.nav-contact {
	background: #e07b1a;
	color: #fff;
	font-size: 15px;
	font-weight: 700;
	padding: 10px 20px;
	border-radius: 6px;
	text-decoration: none;
}

/* Knowledge Base */
.kb {
	padding: 64px 60px;
}
.kb h1 {
	font-size: 42px;
	font-weight: 800;
	margin-bottom: 40px;
}
.grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 22px;
}
.card {
	border: 1px solid #ddd;
	border-radius: 12px;
	padding: 30px;
	display: block;
	transition: box-shadow .2s;
}
.card:hover {
	box-shadow: 0 4px 18px rgba(0,0,0,.09);
}
.badge {
	display: inline-block;
	font-size: 16px;
	font-weight: 700;
	padding: 5px 16px;
	border-radius: 20px;
	color: #fff;
	margin-bottom: 14px;
}
.badge.green {
	background: #1e7c83;
}
.badge.teal {
	background: #1d9e75;
}
.card h3 {
	font-size: 24px;
	font-weight: 700;
	margin-bottom: 10px;
	line-height: 1.3;
}
.card p {
	font-size: 17px;
	color: #555;
	line-height: 1.65;
	margin-bottom: 18px;
}
.read {
	font-size: 17px;
	font-weight: 500;
	color: #1d9e75;
	text-decoration: none;
	display: inline-block;
}
.read:hover {
	text-decoration: underline;
}
.faq-link {
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
}
.faq-arrow {
	font-weight: 700;
}

/* Multi-document trigger on a card */
.read-row {
	display: flex;
	align-items: center;
	gap: 14px;
	flex-wrap: wrap;
}
.browse {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 16px;
	font-weight: 700;
	color: #2f6b5b;
	background: #eaf2ee;
	border: none;
	border-radius: 999px;
	padding: 11px 20px;
	cursor: pointer;
}
.browse:hover {
	background: #dcece4;
}
.browse-arrow {
	font-weight: 700;
}
.doc-count {
	font-size: 14px;
	color: #888;
}

/* ---------------- Document explorer modal ---------------- */
.overlay {
	position: fixed;
	inset: 0;
	background: rgba(17, 24, 22, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	z-index: 1000;
}
.modal {
	background: #fff;
	width: 100%;
	max-width: 920px;
	height: min(640px, 88vh);
	border-radius: 14px;
	box-shadow: 0 24px 64px rgba(0,0,0,.28);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.modal-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 18px 24px;
	border-bottom: 1px solid #eee;
	flex-shrink: 0;
}
.breadcrumb {
	font-size: 14px;
	color: #888;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}
.breadcrumb .sep {
	color: #ccc;
}
.breadcrumb .current {
	color: #111;
	font-weight: 700;
}
.close-btn {
	background: none;
	border: none;
	cursor: pointer;
	font-size: 18px;
	color: #888;
	padding: 4px 8px;
	border-radius: 6px;
}
.close-btn:hover {
	background: #f5f5f5;
	color: #111;
}

.modal-body {
	flex: 1;
	display: flex;
	min-height: 0;
}

/* Sidebar tree */
.sidebar {
	width: 290px;
	flex-shrink: 0;
	border-right: 1px solid #eee;
	background: #fafafa;
	display: flex;
	flex-direction: column;
}
.sidebar-top {
	padding: 16px 18px;
	border-bottom: 1px solid #eee;
}
.sidebar-top .total {
	font-size: 13px;
	font-weight: 700;
	color: #888;
	text-transform: uppercase;
	letter-spacing: .04em;
}
.sidebar-top .all-link {
	display: inline-block;
	margin-top: 8px;
	font-size: 14px;
	font-weight: 600;
	color: #1d9e75;
	text-decoration: none;
}
.sidebar-top .all-link:hover {
	text-decoration: underline;
}

.tree {
	flex: 1;
	overflow-y: auto;
	padding: 8px;
}
.tree-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 12px;
	border-radius: 7px;
	cursor: pointer;
	font-size: 15px;
	font-weight: 600;
	color: #333;
}
.tree-item:hover {
	background: #eee;
}
.tree-item.parent {
	justify-content: space-between;
	color: #111;
}
.tree-item.child {
	font-weight: 500;
	font-size: 14.5px;
}
.tree-item.top-leaf {
	font-weight: 700;
	font-size: 15px;
	color: #111;
}
.tree-item.child.active,
.tree-item.top-leaf.active {
	background: #e6f6f0;
	color: #15795c;
	font-weight: 700;
}
.tree-children {
	padding-left: 18px;
}
.tree-item .arrow {
	font-size: 20px;
	color: #aaa;
	transform: rotate(90deg);
	transition: transform .2s;
}
.tree-item .arrow.open {
	transform: rotate(270deg);
}

/* Content pane */
.content-pane {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 0;
}
.content-scroll {
	flex: 1;
	overflow-y: auto;
	padding: 32px 36px;
	display: flex;
	flex-direction: column;
}
.content-scroll .eyebrow {
	font-size: 13px;
	font-weight: 700;
	letter-spacing: .05em;
	text-transform: uppercase;
	color: #1d9e75;
}
.content-scroll h2 {
	font-size: 26px;
	font-weight: 800;
	margin: 10px 0 0;
}
.content-scroll .body-text {
	margin-top: 14px;
	font-size: 16px;
	line-height: 1.7;
	color: #555;
	max-width: 520px;
}
.download-center {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
}
.content-empty {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 40px;
	color: #888;
	font-size: 16px;
	text-align: center;
}
.btn-download {
	background: #e07b1a;
	color: #fff;
	font-size: 15px;
	font-weight: 700;
	padding: 14px 30px;
	border-radius: 8px;
	text-decoration: none;
}
.btn-download:hover {
	background: #c66b13;
}

@media (max-width: 680px) {
	.overlay { padding: 0; }
	.modal { max-width: 100%; height: 100%; border-radius: 0; }
	.modal-body { flex-direction: column; }
	.sidebar { width: 100%; max-height: 40%; border-right: none; border-bottom: 1px solid #eee; }
	.content-scroll { padding: 22px 20px; }
}

/* FAQ */
.faq {
	padding: 64px 60px;
	border-top: 1px solid #eee;
}
.faq h1 {
	font-size: 38px;
	font-weight: 800;
	text-align: center;
	color: #1d9e75;
	margin-bottom: 36px;
}
.faq-item {
	border-bottom: 1px solid #ddd;
}
.faq-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 0;
	font-size: 19px;
	font-weight: 600;
	cursor: pointer;
}
.arrow {
	font-size: 26px;
	color: #888;
	transform: rotate(90deg);
	display: inline-block;
	transition: transform .2s;
}
.arrow.open {
	transform: rotate(270deg);
}
.faq-body p {
	font-size: 18px;
	color: #444;
	line-height: 1.75;
	padding-bottom: 24px;
}

/* Footer */
footer {
	border-top: 1px solid #eee;
	padding: 52px 60px;
	display: grid;
	grid-template-columns: 2fr 1fr 1fr 1fr;
	gap: 32px;
}
.flogo {
	font-size: 22px;
	font-weight: 800;
	color: #1a2c5b;
	margin-bottom: 14px;
}
footer p {
	font-size: 16px;
	color: #555;
	margin-bottom: 9px;
}
footer h4 {
	font-size: 18px;
	font-weight: 700;
	margin-bottom: 14px;
	color: #111;
}
footer a {
	font-size: 16px;
	color: #555;
	text-decoration: none;
	display: block;
	margin-bottom: 9px;
}
footer a:hover {
	color: #1d9e75;
}
</style>